import { Button } from "@chakra-ui/button"
import { Center, Divider, Heading, HStack, Text, VStack } from "@chakra-ui/layout"
import { Table, Td, Th, Tr, Thead, Tbody } from "@chakra-ui/table"
import {
  useCancelTopup,
  useCarrotCardAccountTopups,
  useCreateTopup,
  useIsAccountVerified,
  useVerifyMicrodeposits,
  usePreLaunchOrActiveCarrotCardAccount
} from "components/carrotcard/hooks/accounts"
import { getCompanyViewLink } from "components/companies/utils/navigationUtils"
import ErrorWrapper from "components/ErrorWrapper"
import Exoskeleton from "components/Exoskeleton"
import Moment from "moment"
import React, { useState } from "react"
import { Company } from "types/Company"
import { Topup } from "types/CarrotCardAccount"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlusSquare } from "@fortawesome/free-solid-svg-icons"
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter
} from "@chakra-ui/modal"
import { useDisclosure } from "@chakra-ui/hooks"
import { Select, Spinner, Input } from "@chakra-ui/react"
import styled from "@emotion/styled"
import CurrencyInput from "react-currency-input-field"
import LaunchInitiation from "components/carrotcard/LaunchInitiation"
import { LaunchMessages } from "components/carrotcard/LaunchMessages"
import { useLaunchHandlers } from "components/carrotcard/hooks/launchMessageHandlers"

const formatDollars = (value: number) => `$${value?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
const formatDollarsWithCents = (value: number) => `$${value?.toLocaleString(undefined, { maximumFractionDigits: 2 })}`

export default function Topups({ company }: { company: Company }): JSX.Element {
  const { data: account, isLoading: isAccountLoading, refetch: refetchAccount } = usePreLaunchOrActiveCarrotCardAccount(
    company.companyId
  )
  const { data: verifiedResult, isSuccess: isVerifiedSuccess } = useIsAccountVerified(account?.id)
  const { data: topups, isLoading: isTopupsLoading, error: topupsError } = useCarrotCardAccountTopups(account?.id)

  const isInitialTopupCheck = (topups) => {
    const anySuccessfulTopups = topups?.some((topup) => topup?.status === "succeeded" || topup?.status == "pending")
    if (topups === undefined || anySuccessfulTopups) {
      return false
    } else {
      return true
    }
  }

  const onePendingTopup = topups?.length < 2 && topups?.some((topup) => topup?.status == "pending")
  const noSuccessfulOrPendingTopups = isInitialTopupCheck(topups)
  const isInitialAndPreLaunch = noSuccessfulOrPendingTopups && !account?.isLaunched
  const showLaunchButton = topups?.some((topup) => topup?.status === "succeeded") && !account?.isLaunched
  const {
    launchSuccess,
    launchError,
    getCompanyError,
    launchedCompanies,
    handleLaunchSuccess,
    handleLaunchError,
    handleGetCompanyError
  } = useLaunchHandlers(refetchAccount)

  return (
    <>
      <LaunchMessages
        launchSuccess={launchSuccess}
        launchError={launchError}
        getCompanyError={getCompanyError}
        launchedCompanies={launchedCompanies}
      />
      {isVerifiedSuccess &&
        !isAccountLoading &&
        !isTopupsLoading &&
        (verifiedResult.verified ? (
          <>
            <Heading size="md" color="gray.400">
              Manage balance
            </Heading>
            <AccountMessages
              accountIsLaunched={account.isLaunched}
              noSuccessfulOrPendingTopups={noSuccessfulOrPendingTopups}
              onePendingTopup={onePendingTopup}
            ></AccountMessages>
            <HStack justifyContent="space-between">
              <AccountInfo companyId={company.companyId} />
              {showLaunchButton && (
                <LaunchInitiation
                  companyId={company.companyId}
                  stripeAccountId={account.id}
                  onLaunchSuccess={handleLaunchSuccess}
                  onLaunchError={handleLaunchError}
                  onGetCompanysError={handleGetCompanyError}
                />
              )}
            </HStack>
            <Divider mt="24px" mb="24px" />
            <Button as="a" size="sm" mr="8px" href={getCompanyViewLink(`${company.companyId}`)}>
              Back
            </Button>
            <Divider mt="24px" mb="24px" />
            <ErrorWrapper error={topupsError}>
              <PriorTopups
                accountId={account.id}
                companyId={company.companyId}
                isInitialAndPreLaunch={isInitialAndPreLaunch}
                isTopupsLoading={isTopupsLoading}
                topups={topups}
              />
            </ErrorWrapper>
          </>
        ) : (
          <>
            <Heading size="md" color="gray.400">
              Verify Stripe
            </Heading>
            <VerifyMicropayments accountId={account.id} />
          </>
        ))}
    </>
  )
}

const ErrorText = ({ children, ...otherProps }) => (
  <Text {...otherProps} color="red">
    {children}
  </Text>
)

function AccountMessages({
  accountIsLaunched,
  noSuccessfulOrPendingTopups,
  onePendingTopup
}: {
  accountIsLaunched: boolean
  noSuccessfulOrPendingTopups: boolean
  onePendingTopup: boolean
}): JSX.Element {
  return (
    <>
      {!accountIsLaunched && (
        <Text color="red" mt="24px">
          Carrot Card is not yet launched
        </Text>
      )}
      {noSuccessfulOrPendingTopups && !accountIsLaunched && (
        <Text fontSize={16}>
          <strong>
            {" "}
            Micro deposits were successfully verified. <br />
            To finish setting up this customer’s stripe balance, please reference&nbsp;
            <u>
              <a
                target="_blank"
                rel="noreferrer"
                href={`https://carrotfertility.atlassian.net/wiki/spaces/SE/pages/1977713101/Initial+Top+Up`}
              >
                stripe account setup.
              </a>
            </u>
          </strong>
        </Text>
      )}
      {onePendingTopup && !accountIsLaunched && (
        <strong>
          <Text>
            The topup was successfully saved and may take 2-5 days to process before displaying properly in the customer
            account.
          </Text>
        </strong>
      )}
    </>
  )
}

function VerifyMicropayments({ accountId }: { accountId: string }): JSX.Element {
  const { mutate, data: verifyMicrodepositsResult, isError, isLoading } = useVerifyMicrodeposits(accountId)
  const [microdeposit1, setMicrodeposit1] = useState<string>("")
  const [microdeposit2, setMicrodeposit2] = useState<string>("")

  const resetMicrodepositFields = () => {
    setMicrodeposit1("")
    setMicrodeposit2("")
  }

  const onVerify = () => {
    const request = { microdeposit1: Number(microdeposit1), microdeposit2: Number(microdeposit2) }
    mutate(request, {
      onSuccess: resetMicrodepositFields,
      onError: resetMicrodepositFields
    })
  }

  return (
    <ErrorWrapper>
      <Exoskeleton>
        {((!isLoading && verifyMicrodepositsResult?.verified === false) || isError) && (
          <ErrorText>
            There was a problem verifying microdeposits: {verifyMicrodepositsResult?.exceptionMessage}
          </ErrorText>
        )}
        <Heading mt="30px" id="verify-stripe-microdeposits" size="sm">
          Verify Stripe Micro Deposits
        </Heading>
        <Text fontSize="16px" color="gray.400">
          Only enter cents with no decimal (ex: `55`)
        </Text>
        <HStack mt="12px">
          <VStack alignItems="left">
            <Input
              width="106px"
              type="number"
              value={microdeposit1}
              onChange={(event) => setMicrodeposit1(event.target.value)}
            />
            <Text>1st deposit</Text>
          </VStack>
          <VStack alignItems="left">
            <Input
              width="106px"
              type="number"
              value={microdeposit2}
              onChange={(event) => setMicrodeposit2(event.target.value)}
            />
            <Text>2nd deposit</Text>
          </VStack>
        </HStack>
        <Button mt="12px" colorScheme="carrotOrange" onClick={onVerify}>
          Verify
        </Button>
      </Exoskeleton>
    </ErrorWrapper>
  )
}

function AccountInfo({ companyId }: { companyId: number }): JSX.Element {
  const { data: account, isLoading: isAccountLoading, error: accountError } = usePreLaunchOrActiveCarrotCardAccount(
    companyId
  )

  return (
    <ErrorWrapper error={accountError}>
      <Exoskeleton isLoading={isAccountLoading}>
        <HStack spacing="150px" mt="80px">
          <VStack alignItems="left">
            <Heading id="stripe-current-balance-label" size="sm">
              Stripe Balance as of today
            </Heading>
            <Text aria-labelledby="stripe-current-balance-label">
              {formatDollarsWithCents(account?.currentBalance)}
            </Text>
          </VStack>
          <VStack alignItems="left">
            <Heading id="stripe-minimum-balance-label" size="sm">
              Stripe Minimum
            </Heading>
            {account?.minimumBalance ? (
              <Text aria-labelledby="stripe-minimum-balance-label">{formatDollars(account?.minimumBalance)}</Text>
            ) : (
              <ErrorText aria-labelledby="stripe-target-balance-label">Not set</ErrorText>
            )}
          </VStack>
          <VStack alignItems="left">
            <Heading id="stripe-target-balance-label" size="sm">
              Stripe Target
            </Heading>
            {account?.targetBalance ? (
              <Text aria-labelledby="stripe-target-balance-label">{formatDollars(account.targetBalance)}</Text>
            ) : (
              <ErrorText aria-labelledby="stripe-target-balance-label">Not set</ErrorText>
            )}
          </VStack>
        </HStack>
      </Exoskeleton>
    </ErrorWrapper>
  )
}

function PriorTopups({
  accountId,
  companyId,
  isInitialAndPreLaunch,
  topups,
  isTopupsLoading
}: {
  accountId: string
  companyId: number
  isInitialAndPreLaunch: boolean
  topups: Topup[]
  isTopupsLoading: boolean
}): JSX.Element {
  const { refetch: refetchAccount } = usePreLaunchOrActiveCarrotCardAccount(companyId)
  const { mutate: cancelTopup } = useCancelTopup(accountId)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const finalRef = React.useRef()

  const openModal = () => {
    onOpen()
    refetchAccount()
  }
  const closeModal = () => {
    onClose()
    refetchAccount()
  }

  const canCancel = (status) => status === "pending"
  return (
    <Exoskeleton isLoading={isTopupsLoading}>
      <HStack justifyContent="space-between">
        <Heading id="previous-topups-table-heading" size="md">
          Previous top-ups (Last {topups?.length})
        </Heading>
        {isInitialAndPreLaunch ? (
          <Button onClick={openModal} leftIcon={<FontAwesomeIcon icon={faPlusSquare} />} variant="outline">
            Initial top-up
          </Button>
        ) : (
          <Button onClick={openModal} leftIcon={<FontAwesomeIcon icon={faPlusSquare} />} variant="outline">
            Initiate manual top-up
          </Button>
        )}
      </HStack>
      <Text as="em" fontSize="14px">
        Refer to the transaction’s status to understand which ones are still pending (not yet reflected in the Stripe
        balance).
      </Text>
      <Table aria-labelledby="previous-topups-table-heading" variant="unstyled" size="sm" mt="44px">
        <Thead>
          <Tr bgColor="gray.200">
            <Th>Date</Th>
            <Th>Initiated by</Th>
            <Th>Top-up mechanism </Th>
            <Th>Top-up amount</Th>
            <Th>Status</Th>
            <Th>&nbsp;</Th>
          </Tr>
        </Thead>
        <Tbody>
          {topups?.map(({ amount, createdDate, status, initiatedBy, mechanism, id }, i) => (
            <Tr key={i}>
              <Td>{Moment(createdDate).format("MM/DD/YYYY")}</Td>
              <Td>{initiatedBy?.userName || "N/A"}</Td>
              <Td>{mechanism || "N/A"}</Td>
              <Td>{formatDollarsWithCents(amount)}</Td>
              <Td textTransform="capitalize">{status}</Td>
              <Td>
                {canCancel(status) ? (
                  <Button size="sm" colorScheme="blue" variant={"link"} onClick={() => cancelTopup(id)}>
                    Cancel
                  </Button>
                ) : (
                  <Text fontSize="16px" minWidth="6ch">
                    &nbsp;
                  </Text>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <CreateTopupModal
        finalRef={finalRef}
        isOpen={isOpen}
        onClose={closeModal}
        accountId={accountId}
        isInitialAndPreLaunch={isInitialAndPreLaunch}
        companyId={companyId}
      />
    </Exoskeleton>
  )
}

const CustomAmountInput = styled(CurrencyInput)`
  :disabled {
    background-color: rgba(0, 0, 0, 0.1);
  }
`

function CreateTopupModal({ finalRef, isOpen, onClose, accountId, companyId, isInitialAndPreLaunch }): JSX.Element {
  const { data: account, isLoading: isAccountLoading, error: accountError } = usePreLaunchOrActiveCarrotCardAccount(
    companyId
  )
  const { mutate, isError, isLoading: processingSave } = useCreateTopup(accountId)
  const [mechanism, setMechanism] = useState<string>(isInitialAndPreLaunch ? "initial" : "")
  const [customAmount, setCustomAmount] = useState<string>("")

  const isCustom = (mechanism) => mechanism === "custom"
  const isAboveTarget = account?.currentBalance >= account?.targetBalance

  const onMechanismSelect = (mechanism) => {
    setMechanism(mechanism)
    if (!isCustom(mechanism)) {
      setCustomAmount("")
    }
  }

  const onSave = () => {
    const customAmountFormatted = customAmount.replace(/[^\d.-]/g, "")
    if (isNaN(Number(customAmountFormatted))) {
      return
    }
    const request =
      isCustom(mechanism) || isInitialAndPreLaunch
        ? { mechanism, amount: Number(customAmountFormatted) }
        : { mechanism }

    mutate(request, {
      onSuccess: () => {
        setMechanism("")
        setCustomAmount("")
        onClose()
      }
    })
  }

  return (
    <Modal finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Initiate manual top-up</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <ErrorWrapper error={accountError}>
            <Exoskeleton isLoading={isAccountLoading}>
              {isError && (
                <ErrorText>There was a problem trying to perform the top-up. Please try again or cancel</ErrorText>
              )}
              <Text as="b">How would you like to top-up?</Text>
              {isInitialAndPreLaunch ? (
                <>
                  <Text paddingBottom="30px">Initial top-up</Text>
                  <Text as="b">Amount</Text>
                </>
              ) : (
                <>
                  <Select
                    mb="32px"
                    placeholder="Select"
                    value={mechanism}
                    onChange={({ target: { value } }) => onMechanismSelect(value)}
                  >
                    <option value="target" disabled={isAboveTarget}>
                      {isAboveTarget
                        ? "Balance is greater than target"
                        : `To target balance: ${formatDollars(account?.targetBalance)}`}
                    </option>
                    <option value="custom">Top-up custom amount</option>
                  </Select>
                  <Text as="b">Custom top-up amount</Text>
                </>
              )}
              <Input
                as={CustomAmountInput}
                decimalsLimit={2}
                placeholder="$"
                onChange={(value) => setCustomAmount(value.target.value)}
                isDisabled={!isCustom(mechanism) && !isInitialAndPreLaunch}
              />
            </Exoskeleton>
          </ErrorWrapper>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={onClose} isDisabled={processingSave}>
            Close
          </Button>
          <Button colorScheme="carrotOrange" onClick={onSave} isDisabled={processingSave}>
            Save
          </Button>
        </ModalFooter>
        {processingSave && (
          <Center>
            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="carrotOrange.500" size="xl" />
          </Center>
        )}
      </ModalContent>
    </Modal>
  )
}
